import { useCallback, useEffect, useRef, useState } from 'react';
import { getParticipantToken } from './api';
import { getSupabase, isSupabaseConfigured } from './supabaseClient';
import { subscribeToTable } from './realtime';
import type { GameState, Participant } from './types';
import { LAYOUT_PREVIEW_SESSION_ID, useLayoutPreview } from './layoutPreviewContext';

export interface GameStateHook {
  state: GameState | null;
  participants: Participant[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface GameStatePayload {
  new?: Partial<GameState>;
  old?: Partial<GameState>;
  eventType?: 'INSERT' | 'UPDATE' | 'DELETE';
}

interface ParticipantPayload {
  new?: Participant;
  old?: Participant;
  eventType?: 'INSERT' | 'UPDATE' | 'DELETE';
}

export function useGameState(sessionId: string | null): GameStateHook {
  const previewCfg = useLayoutPreview();
  const previewLive = !!(
    previewCfg &&
    sessionId != null &&
    sessionId === LAYOUT_PREVIEW_SESSION_ID
  );

  const [state, setState] = useState<GameState | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(!previewLive);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  /** Evita lista partecipanti obsoleta quando il round cambia prima dei DELETE Realtime. */
  const prevRoundRef = useRef<number | null>(null);
  const prevStatusRef = useRef<string | null>(null);

  const fetchAll = useCallback(async (sid: string) => {
    if (!isSupabaseConfigured()) return;
    const supabase = getSupabase();
    try {
      const [{ data: gs, error: gsErr }, { data: ps, error: psErr }] = await Promise.all([
        supabase.from('game_state').select('*').eq('session_id', sid).maybeSingle(),
        supabase
          .from('participants')
          .select('*')
          .eq('session_id', sid)
          .order('slot', { ascending: true }),
      ]);
      if (cancelledRef.current) return;
      if (gsErr) setError(gsErr.message);
      else setState((gs as GameState | null) ?? null);
      if (psErr) setError(psErr.message);
      else setParticipants((ps as Participant[] | null) ?? []);
    } catch (e) {
      if (!cancelledRef.current) setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const refresh = useCallback(async () => {
    if (previewLive) return;
    if (!sessionId) return;
    await fetchAll(sessionId);
  }, [sessionId, fetchAll, previewLive]);

  useEffect(() => {
    if (!previewLive || !previewCfg) return;
    setState(previewCfg.state);
    setParticipants(previewCfg.participants);
    setLoading(false);
    setError(null);
  }, [previewLive, previewCfg, previewCfg?.state, previewCfg?.participants]);

  useEffect(() => {
    if (previewLive) return;
    prevRoundRef.current = null;
    prevStatusRef.current = null;
  }, [sessionId, previewLive]);

  /** Nuovo round (`START_ROUND`): risincronizza sempre i partecipanti dal DB. */
  useEffect(() => {
    if (previewLive) return;
    if (!sessionId || !state) return;
    const r = state.round;
    const prev = prevRoundRef.current;
    if (prev !== null && prev !== r) {
      void fetchAll(sessionId);
    }
    prevRoundRef.current = r;
  }, [sessionId, state?.round, fetchAll]);

  /** Lobby vuota / reset round: `IDLE` o `WAITING` senza incremento round (es. wipe + start). */
  useEffect(() => {
    if (previewLive) return;
    if (!sessionId) return;
    const st = state?.status ?? null;
    const prev = prevStatusRef.current;
    if (prev !== null && st !== prev && (st === 'IDLE' || st === 'WAITING_FOR_PLAYERS')) {
      void fetchAll(sessionId);
    }
    prevStatusRef.current = st;
  }, [sessionId, state?.status, fetchAll]);

  useEffect(() => {
    if (previewLive) return;
    if (!sessionId) {
      setLoading(false);
      return;
    }
    if (!isSupabaseConfigured()) {
      setError('Supabase non configurato (manca VITE_SUPABASE_URL/ANON_KEY).');
      setLoading(false);
      return;
    }
    cancelledRef.current = false;

    async function bootstrap() {
      setLoading(true);
      await fetchAll(sessionId!);
      if (!cancelledRef.current) setLoading(false);
    }

    void bootstrap();

    const cleanups: Array<() => void> = [];

    cleanups.push(
      subscribeToTable(
        { table: 'game_state', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const p = payload as GameStatePayload;
          if (!p.new) return;
          setState((prev) => {
            const n = p.new as Partial<GameState>;
            if (!prev) return { ...n } as GameState;
            return { ...prev, ...n } as GameState;
          });
        },
      ),
    );

    cleanups.push(
      subscribeToTable(
        { table: 'participants', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const p = payload as ParticipantPayload;
          setParticipants((prev) => {
            if (p.eventType === 'DELETE') {
              if (p.old?.id) {
                return prev.filter((x) => x.id !== p.old!.id);
              }
              queueMicrotask(() => {
                void fetchAll(sessionId);
              });
              return [];
            }
            if (p.eventType === 'INSERT' && p.new?.id) {
              const others = prev.filter((x) => x.id !== p.new!.id);
              return [...others, p.new!].sort((a, b) => a.slot - b.slot);
            }
            if (p.eventType === 'UPDATE' && p.new) {
              const id = p.new.id ?? p.old?.id;
              if (!id) return prev;
              const existing = prev.find((x) => x.id === id);
              const merged = { ...(existing as Participant), ...p.new } as Participant;
              const others = prev.filter((x) => x.id !== id);
              return [...others, merged].sort((a, b) => a.slot - b.slot);
            }
            return prev;
          });
        },
      ),
    );

    return () => {
      cancelledRef.current = true;
      for (const c of cleanups) c();
    };
  }, [sessionId, fetchAll]);

  /** Su Vercel Hobby senza pg_cron, nessuno chiamava round-tick: pulse leggero dai client connessi. */
  useEffect(() => {
    if (previewLive) return;
    if (!sessionId || !state) return;
    if (!['WRITING', 'VOTING', 'GENERATING', 'ENDED'].includes(state.status)) return;

    function pulse(): void {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      const pt = getParticipantToken();
      if (pt) headers.authorization = `Bearer ${pt}`;
      void fetch('/api/jobs/round-tick', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }

    pulse();
    const id = window.setInterval(pulse, 5000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pulse su WRITING/VOTING/GENERATING/ENDED (auto IDLE)
  }, [sessionId, state?.status]);

  /** Durante GENERATING Realtime può non aggiornare il client: polling REST come fallback (maxi + partecipanti). */
  useEffect(() => {
    if (previewLive) return;
    if (!sessionId || !state) return;
    if (state.status !== 'GENERATING') return;
    void fetchAll(sessionId);
    const id = window.setInterval(() => {
      void fetchAll(sessionId);
    }, 2500);
    return () => window.clearInterval(id);
  }, [sessionId, state?.status, fetchAll]);

  return { state, participants, loading, error, refresh };
}
